/*
 * Copyright (c) 2019 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import qs from 'qs';
import {isEUProduction} from '../environment/environment';
import {applyInterceptor} from '../services/ReadOnlyInterceptor';
import {
  Asset,
  MaintenanceSchedule,
  MaintenanceServiceItem
} from '../types/entities';
import {
  LoadMaintenanceSchedulesParams,
  LoadMaintenanceSchedulesResult,
  LoadMaintenanceServiceItemParams,
  LoadMaintenanceServiceItemResult,
  Module,
  Service
} from '../types/services';
import {
  convertOdometer,
  getUserUnit
} from '../units/helpers';
import {
  Unit
} from '../units/units';
import {loadAll} from './Connect';
import {
  client,
  url
} from './ConnectUtils';

enum OdometerProperty {
  odometer = 'odometer',
  odometerDue = 'odometerDue',
  intervalOdometer = 'intervalOdometer',
  reminderOdometer = 'reminderOdometer',
  lastServiceOdometer = 'lastServiceOdometer',
  nextServiceOdometer = 'nextServiceOdometer',
  nextReminderOdometer = 'nextReminderOdometer'
}

type OdometerProperties = {
  SERVICE_ITEM: OdometerProperty[],
  ASSET: OdometerProperty[],
  ASSET_SERVICE_STATUS: OdometerProperty[],
  ASSET_SERVICE_LOG: OdometerProperty[]
}

type OdometerLike = {
  OdometerProperty?: any;
  [key: string]: any;
}

type ConvertOdometerUnitsOptions = {
  reverse?: boolean,
  round?: boolean
}

export const ODOMETER_PROPERTIES = {
  SERVICE_ITEM : [
    OdometerProperty.intervalOdometer,
    OdometerProperty.reminderOdometer
  ],
  ASSET : [
    OdometerProperty.odometer
  ],
  ASSET_SERVICE_STATUS : [
    OdometerProperty.lastServiceOdometer,
    OdometerProperty.nextServiceOdometer,
    OdometerProperty.nextReminderOdometer
  ],
  ASSET_SERVICE_LOG : [
    OdometerProperty.odometer,
    OdometerProperty.odometerDue
  ]
};

/**
 * setMaintenanceReadOnlyMode sets a restriction to provided restrctions within the function
 * by subscribing to intercepter with applyInterceptor function.
 *
 * ```typescript
 *  import setMaintenanceReadOnlyMode from '@ion/core/src/services/Maintenance';
 *
 *  setMaintenanceReadOnlyMode(true) // sets restrction on defined urls in restrction array
 *
 * ```
 */
export const setMaintenanceReadOnlyMode = (isReadOnly = false) => {
  if (isReadOnly) {
    const restrictions = [
      url('/schedules/:id', Service.Maintenance),
      url('/schedules', Service.Maintenance),
      url('/serviceitems', Service.Maintenance),
      url('/serviceitems/:id', Service.Maintenance),
      url('/servicelogs', Service.Maintenance),
      url('/servicelogs/:id', Service.Maintenance)
    ];
    applyInterceptor({client, restrictions, module: Module.MaintenanceModule, isReadOnly});
  }
};

/**
 * @function loadMaintenanceDashboard
 *
 * Provides a paginated search implementation against CTC Asset Services which supports
 * optional sorting, filtering, and hierarchical support.
 *
 * ```typescript
 *
 *    import {loadMaintenanceDashboard} from '@ion/core/src/services/Connect';
 *
 *    // retrieve the first 20 assets within the given account, sorted by assetType descending...
 *    const {assets, totalCount} = await loadMaintenanceDashboard({
 *      sortOn    : `-${AssetSearchableProperty.assetType}` as AssetSort,
 *      accountId : 321
 *    });
 *
 *    // retrieve the first 100 assets within the entire account hierarchy whose name contains
 *    // 'asset', and sorting by ascending by name and descending by lastModifiedOn ...
 *    const {assets, totalCount} = await loadMaintenanceDashboard({
 *      filterOn         : {
 *        [AssetSearchableProperty.name]: '*asset*'
 *      },
 *      sortOn         : [
 *        `+${AssetSearchableProperty.name}`,
 *        `-${AssetSearchableProperty.lastModifiedOn}`
 *      ] as AssetSort[]
 *      accountId        : 321,
 *      includeSubGroups : true,
 *      pageId           : 1,
 *      pageSize         : 100
 *    });
 *
 * ```
 *
 * @param   {number} accountId The id of the account from which to retrieve Asset entities.
 * @param   {number} pageId The page which is to be loaded.
 * @param   {number} pageSize The size of the page to be loaded.
 * @param   {Array<string>} sortOn The field on which the results are to be sorted.
 * @param   {string} filterOn An object containing additional searchable properties from
 *          which the searchTerms request is to be based.
 * @returns Promise<LoadMaintenanceDashboardResult>
 */
export const loadMaintenanceDashboard = async({
  accountId,
  pageId = 1,
  pageSize = 20,
  sortOn = {},
  filterOn = {},
  hierarchy = false,
  convertUnits = false,
  suite = true
}) => {
  const query: {
    hierarchy?: boolean,
    pg: number,
    pgsize: number,
    sort?: {},
    filterOn?: {},
    filter?: {},
    suite?: boolean
  } = {
    hierarchy : hierarchy,
    pg        : pageId,
    pgsize    : pageSize,
    sort      : sortOn
  };
  if (suite) {
    query.suite = suite;
  }
  const endpoint = url(`/accounts/${accountId}/dashboard?${qs.stringify(query)}`, Service.Maintenance);
  const {data, status} = await client.post(endpoint, JSON.stringify(
    filterOn
  ));
  if (convertUnits) {
    const items = data?.rows;
    if (items?.length) {
      data.rows = convertListOdometerUnits(items, ODOMETER_PROPERTIES.ASSET);
    }
  }
  return {
    items      : data.rows,
    totalCount : data.count,
    status
  };
};

/**
 * This provids the api which will render the schedule service status based on account, and this will be providing
 * list of schedule service status when we hover on the schedule status column from the maintanance
 * dashboard pgrid
 * @param   {number} accountId The id of the account from which to retrieve Asset entities.
 * @param   {number} pageId The page which is to be loaded.
 * @param   {number} pageSize The size of the page to be loaded.
 * @param   {Array<string>} sortOn The field on which the results are to be sorted.
 * @param   {string} filterOn An object containing additional searchable properties from
 *          which the searchTerms request is to be based.
 */
export const loadScheduleServiceStatus = async({
  accountId,
  pageId = 1,
  pageSize = 20,
  sortOn = {},
  filterOn = {},
  hierarchy = false,
  suite = true
}) => {
  const query = {
    hierarchy : hierarchy,
    pg        : pageId,
    pgsize    : pageSize,
    sort      : sortOn,
    filter    : filterOn,
    suite     : suite
  };
  //uses the account id to fetch the records
  const endpoint = url(`/accounts/${accountId}/dashboard?${qs.stringify(query)}`, Service.Maintenance);
  const {data, status} = await client.get(endpoint);
  return {
    items : data?.rows,
    status
  };
};

/**
 * Provides a convenience API which allows for retrieving all dashboard items for every page
 * within the given parameters. This function effectively allows for bypassing manual
 * pagination and eagerly loading all schedules in a single pseudo async call. Generally
 * this function is only to be used for performing exports of dashboard items.
 *
 * @param params The parameters against which the dashboard items are to be retrieved.
 */
export const loadAllMaintenanceDashboardItems = async(params) => {
  const items = await loadAll(loadMaintenanceDashboard, params, 'items');
  return items;
};

/**
 * @function loadServiceStatus
 *
 * Uses the asset's id to fetch the service status of that asset.
 *
 * @param  {number} assetId id of the asset for which to get the service status
 */
export const loadServiceStatus = async(assetId: number, convertUnits = false) => {
  const endpoint = url(`/assets/${assetId}/servicestatus`, Service.Maintenance);
  const response = await client.get(endpoint);
  if (convertUnits) {
    const items = response?.data?.rows;
    if (items?.length) {
      response.data.rows = convertListOdometerUnits(items, ODOMETER_PROPERTIES.ASSET_SERVICE_STATUS);
    }
  }
  return response;
};

/**
 * @function loadServiceLogs
 *
 * Uses the asset's id to fetch the service logs for that asset.
 *
 * @param  {number} assetId id of the asset for which to get the service status
 */
export const loadServiceLogs = async(assetId: number, convertUnits = false) => {
  const endpoint = url(`/assets/${assetId}/servicelogs`, Service.Maintenance);
  const response = await client.get(endpoint);
  if (convertUnits) {
    const items = response?.data?.rows;
    if (items?.length) {
      response.data.rows = convertListOdometerUnits(items, ODOMETER_PROPERTIES.ASSET);
    }
  }
  return response;
};

/**
 * @function createServiceLog
 *
 * Provides the Maintenance Schedule Pagination API which allows for retrieving Maintenance
 * Schedules based on the provided LoadMaintenanceSchedulesParams.
 *
 * @param  {LoadMaintenanceSchedulesParams} params The parameters by which the Maintenance
 *         Schedules are to be retrieved.
 */
export const createServiceLog = async(
  params,
  convertUnits = false
): Promise<any> => {
  if (convertUnits) {
    params = convertOdometerUnits(params, ODOMETER_PROPERTIES.ASSET_SERVICE_LOG, {reverse: true});
  }
  const endpoint = url('/servicelogs', Service.Maintenance);
  const response = await client.post(endpoint, JSON.stringify({
    ...params
  }));
  return response;
};

/**
 * @function editServiceLog
 *
 * Provides the Maintenance Schedule Pagination API which allows for retrieving Maintenance
 * Schedules based on the provided LoadMaintenanceSchedulesParams.
 *
 * @param  {LoadMaintenanceSchedulesParams} params The parameters by which the Maintenance
 *         Schedules are to be retrieved.
 */
export const editServiceLog = async(
  params,
  assetServiceLogId,
  convertUnits = false
): Promise<any> => {
  if (convertUnits) {
    params = convertOdometerUnits(params, ODOMETER_PROPERTIES.ASSET_SERVICE_LOG, {reverse: true});
  }
  const endpoint = url(`/servicelogs/${assetServiceLogId}`, Service.Maintenance);
  const response = await client.put(endpoint, JSON.stringify({
    ...params
  }));
  return response;
};

/**
 * The deleteServiceLog function deletes an existing Schedule history
 *
 * ```typescript
 *
 *  import {deleteServiceLog} from '@ion/core/src/services/Maintenance.ts';
 *  const response = await deleteServiceLog(assetServiceLogId);
 * ```
 */
export const deleteServiceLog = async(
  assetServiceLogId: number
): Promise<boolean> => {
  const response = await client.delete(url(`/servicelogs/${assetServiceLogId}`, Service.Maintenance));
  return response;
};

/**
 * @function getInheritedSchedules
 *
 * Loads the schedules for the given group id as well as all schedules inherited
 * from the groups parent account hierarchy.
 *
 * @param  {number} groupId id of the group for which to schedules are to be retrieved.
 */
export const getInheritedSchedules = async(groupId: number) => {
  const endpoint = url(`/accounts/${groupId}/schedules?inherited=true`, Service.Maintenance);
  const response = await client.get(endpoint);
  return response.data?.rows;
};

/**
 * @function loadSchedules
 *
 * Provides the Maintenance Schedule Pagination API which allows for retrieving Maintenance
 * Schedules based on the provided LoadMaintenanceSchedulesParams.
 *
 * @param  {LoadMaintenanceSchedulesParams} params The parameters by which the Maintenance
 *         Schedules are to be retrieved.
 */
export const loadSchedules = async(
  params: LoadMaintenanceSchedulesParams
): Promise<LoadMaintenanceSchedulesResult> => {
  const endpoint = url(constructQueryString('/schedules/search', params), Service.Maintenance);
  const results = await client.post(endpoint, JSON.stringify({
    accountId : params.accountId,
    ...params.filters
  }));
  const {rows, count} = results?.data;
  return {schedules: rows, totalCount: count};
};

/**
 * Provides a convenience API which allows for retrieving all schedules for every page
 * within the given parameters. This function effectively allows for bypassing manual
 * pagination and eagerly loading all schedules in a single pseudo async call. Generally
 * this function is only to be used for performing exports of a schedules.
 *
 * @param {LoadMaintenanceSchedulesParams} params The parameters against which the schedules
 *         are to be retrieved.
 */
export const loadAllMaintenanceSchedules = async(params: LoadMaintenanceSchedulesParams) => {
  const schedules = await loadAll(loadSchedules, params, 'schedules');
  return schedules;
};

/**
 * @method createSchedule
 */
export const createSchedule = async(schedule: MaintenanceSchedule) => {
  const endpoint = url('/schedules', Service.Maintenance);
  const response = await client.post(endpoint, JSON.stringify(schedule));
  return response?.data;
};

/**
 * @method getSchedule
 */
export const getSchedule = async(scheduleId: number) => {
  const endpoint = url(`/schedules/${scheduleId}`, Service.Maintenance);
  const response = await client.get(endpoint);
  return response?.data;
};

/**
 * @method updateSchedule
 */
export const updateSchedule = async(schedule: MaintenanceSchedule) => {
  const endpoint = url(`/schedules/${schedule.scheduleId}`, Service.Maintenance);
  const response = await client.put(endpoint, JSON.stringify(schedule));
  return response?.data;
};

/**
 * @method deleteSchedule
 */
export const deleteSchedule = async(scheduleId: number) => {
  const endpoint = url(`/schedules/${scheduleId}`, Service.Maintenance);
  const response = await client.delete(endpoint);
  return response?.data;
};

/**
 * @method assignSchedule
 */
export const assignSchedule = async(asset: Asset, schedule: MaintenanceSchedule) => {
  const endpoint = url(`/assets/${asset.id}/schedules`, Service.Maintenance);
  const response = await client.put(endpoint, JSON.stringify({
    ...schedule,
    assetId   : asset.id,
    assetName : asset.name
  }));
  return response?.data;
};

/**
 * @function deleteAssetMaintenanceSchedule
 *
 * Un-assigns a schedule from the given asset, returning the result.
 */
export const deleteAssetMaintenanceSchedule = async(assetId: number) => {
  const response = await client.delete(url(`/assets/${assetId}`, Service.Maintenance));
  return response?.data;
};

/**
 * @function loadServiceItems
 *
 * Provides the Maintenance Service Items Pagination API which allows for retrieving Maintenance
 * Service Items based on the provided LoadMaintenanceServiceItemParams.
 *
 * @param  {LoadMaintenanceServiceItemParams} params The parameters by which the Maintenance
 *         Service Items are to be retrieved.
 */
export const loadServiceItems = async(
  params: LoadMaintenanceServiceItemParams
): Promise<LoadMaintenanceServiceItemResult> => {
  const endpoint = url(constructQueryString('/serviceitems/search', params), Service.Maintenance);
  const results = await client.post(endpoint, JSON.stringify({
    accountId : params.accountId,
    ...params.filters
  }));
  const {rows, count} = results?.data;
  return {
    serviceItems : params.convertUnits
      ? convertListOdometerUnits(rows, ODOMETER_PROPERTIES.SERVICE_ITEM, {round: true})
      : rows,
    totalCount : count
  };
};

/**
 * Provides a convenience API which allows for retrieving all schedules for every page
 * within the given parameters. This function effectively allows for bypassing manual
 * pagination and eagerly loading all service items in a single pseudo async call.
 * Generally this function is only to be used for performing exports of a service
 * items.
 *
 * @param {LoadMaintenanceServiceItemParams} params The parameters against which the
 *         service items are to be retrieved.
 */
export const loadAllServiceItems = async(params: LoadMaintenanceServiceItemParams) => {
  const serviceItems = await loadAll(loadServiceItems, params, 'serviceItems');
  return serviceItems;
};

/**
 * @function createServiceItem
 *
 * Creates a new Maintenance Service Item, returning the result.
 */
export const createServiceItem = async(
  service: MaintenanceServiceItem, convertUnits = false): Promise<MaintenanceServiceItem> => {
  const endpoint = url('/serviceitems', Service.Maintenance);
  const item = convertUnits
    ? convertOdometerUnits(service, ODOMETER_PROPERTIES.SERVICE_ITEM, {reverse: true, round: true})
    : service;
  const response = await client.post(endpoint,
    JSON.stringify(item)
  );
  return response?.data;
};

/**
 * @function getServiceItem
 *
 * Retrieves an existing Maintenance Service Item, returning the result.
 */
export const getServiceItem = async(
  serviceItemId: number, convertUnits = false): Promise<MaintenanceServiceItem> => {
  const endpoint = url(`/serviceitems/${serviceItemId}`, Service.Maintenance);
  const response = await client.get(endpoint);
  const result   = response?.data;
  return convertUnits
    ? convertOdometerUnits(result, ODOMETER_PROPERTIES.SERVICE_ITEM, {round: true})
    : result;
};

/**
 * @function updateServiceItem
 *
 * Updates an existing Maintenance Service Item, returning the result.
 */
export const updateServiceItem = async(
  service: MaintenanceServiceItem, convertUnits = false): Promise<MaintenanceServiceItem> => {
  const endpoint = url(`/serviceitems/${service.serviceItemId}`, Service.Maintenance);
  const item = convertUnits
    ? convertOdometerUnits(service, ODOMETER_PROPERTIES.SERVICE_ITEM, {reverse: true, round: true})
    : service;
  const response = await client.put(endpoint, JSON.stringify(item));
  return response?.data;
};

/**
 * @function deleteServiceItem
 *
 * Deletes an existing Maintenance Service Item, returning the result.
 */
export const deleteServiceItem = async(service: MaintenanceServiceItem): Promise<MaintenanceServiceItem> => {
  const response = await client.delete(url(`/serviceitems/${service.serviceItemId}`, Service.Maintenance));
  return response?.data;
};

/**
 * @function constructQueryString
 *
 * ```typescript
 *
 *    import {constructQueryString} from '@ion/core/src/services/Maintenance';
 *
 *    const uri = constructQueryString('/accounts/321/serviceitems', {
 *      pageId: 4,
 *      pageSize: 40,
 *      sort: 'name',
 *      hierarchy: true
 *    });
 *    // '/accounts/321/serviceitems?hierarchy=true&pg=4&pgsize=40&sort=name'
 *
 * ```
 *
 * Constructs the URL with the provided Query Parameters (if any) and returns
 * the result.
 *
 *
 * @param  {string} uri The Maintenance Service URI to which the query parameters are to
 *         be added.
 * @param  {LoadMaintenanceSchedulesParams} params The parameters by which the Maintenance
 *         Schedules are to be retrieved.
 */
export const constructQueryString = (uri: string, options: LoadMaintenanceSchedulesParams = {}): string => {
  const {pageId, pageSize, sort, hierarchy, inherited} = options;
  let query = '';
  if (hierarchy && inherited) {
    query = '?hierarchy=true&inherited=true';
  } else if (hierarchy) {
    query = '?hierarchy=true';
  } else if (inherited) {
    query = '?inherited=true';
  }
  if (pageId) {
    const params = `${query}${!query ? '?' : '&'}pg=${pageId}&pgsize=${pageSize}${sort ? `&sort=${sort}` : ''}`;
    return `${uri}${params}`;
  }
  return uri + query;
};

/**
 * @private
 *
 * Provides a utility which converts a collection of odometer units to the given users units
 * of measure. This utility is used when a service APIs convertUnits parameter is set to true.
 */
const convertListOdometerUnits = (
  entities: OdometerLike[],
  properties: OdometerProperty[],
  options?: ConvertOdometerUnitsOptions
): OdometerLike[] => {
  return entities.map((entity: OdometerLike) => convertOdometerUnits(entity, properties, options));
};

/**
 * Given an entity and list of Odometer Properties, converts the entities odometer values to
 * the users default units of measure.
 */
export const convertOdometerUnits = (
  entity: OdometerLike,
  properties: OdometerProperty[],
  options?: ConvertOdometerUnitsOptions
): OdometerLike => {
  const reverse      = Boolean(options?.reverse);
  const round        = Boolean(options?.round);
  const units        = getUserUnit();
  const uom          = isEUProduction() ? Unit.METRIC : Unit.US;
  const odometerUOM  = entity.odometerUOM || uom;
  entity.odometerUOM = reverse ? uom : odometerUOM;
  properties.forEach((property: string) => {
    const value = entity[property];
    if (!isNil(value)) {
      const from = !reverse ? odometerUOM : units;
      const to   = !reverse ? units : uom;
      entity[property] = convertOdometer(value, from, to, reverse);
      if (round) {
        entity[property] = Math.round(entity[property]);
      }
    }
  });
  return entity;
};
